/**
 * Binds list state to the URL.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 5.15
 */
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';

import {
  buildSearchParams,
  nextSort,
  parseTableState,
  type SortState,
  type TableState,
} from './table-state';

export interface UseTableStateResult {
  state: TableState;
  /** Applies a sort click: ascending → descending → unsorted. */
  toggleSort: (field: string) => void;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  /** Replaces one filter. An empty string removes it. */
  setFilter: (key: string, value: string) => void;
  /**
   * Clears every filter.
   *
   * Keys listed in `preserveKeys` survive: some state rides in the query string
   * without being a filter — the pipeline's table/board choice, for instance —
   * and clearing filters must not silently switch the user's view.
   */
  clearFilters: () => void;
}

export function useTableState(
  allowedSortFields: readonly string[],
  preserveKeys: readonly string[] = [],
): UseTableStateResult {
  const [searchParams, setSearchParams] = useSearchParams();

  const state = useMemo(
    () => parseTableState(searchParams, allowedSortFields),
    [searchParams, allowedSortFields],
  );

  const write = useCallback(
    (next: TableState) => {
      /* `replace` so that changing a filter does not add a history entry per
         keystroke — Back should leave the list, not walk through every
         intermediate filter state. */
      setSearchParams(buildSearchParams(next), { replace: true });
    },
    [setSearchParams],
  );

  const toggleSort = useCallback(
    (field: string) => {
      const sort: SortState | null = nextSort(state.sort, field);
      /* Back to page 1: row 51 of the old ordering is meaningless in the new
         one, and staying on page 3 of a re-sorted list looks like data loss. */
      write({ ...state, sort, page: 1 });
    },
    [state, write],
  );

  const setPage = useCallback(
    (page: number) => {
      write({ ...state, page });
    },
    [state, write],
  );

  const setPageSize = useCallback(
    (pageSize: number) => {
      /* Also back to page 1: page 3 of 50-row pages is past the end of a
         100-row paging. */
      write({ ...state, pageSize, page: 1 });
    },
    [state, write],
  );

  const setFilter = useCallback(
    (key: string, value: string) => {
      /* Rebuilt without the key rather than spread-then-delete: an empty value
         means "no filter", and filtering the entries expresses that without
         mutating a copy. */
      const filters = Object.fromEntries(
        Object.entries({ ...state.filters, [key]: value }).filter(
          ([, entry]) => entry !== '',
        ),
      );

      write({ ...state, filters, page: 1 });
    },
    [state, write],
  );

  const clearFilters = useCallback(() => {
    /* Preserved keys stay: they share the query string with the filters but are
       not filters, so clearing must leave them alone. */
    const filters = Object.fromEntries(
      Object.entries(state.filters).filter(([key]) => preserveKeys.includes(key)),
    );

    write({ ...state, filters, page: 1 });
  }, [state, write, preserveKeys]);

  return { state, toggleSort, setPage, setPageSize, setFilter, clearFilters };
}
